import * as React from "react";
import { useMemo, useState } from "react";
import { connect } from "react-redux";
import {Link, useNavigate} from "react-router-dom";
import { bindActionCreators, Dispatch } from "redux";
import { IUserInfo, UserTypeEnum } from "../../model/UserInfo.model";
import { prominentButtonStyle } from "../common/buttonStyles";
import { RaisedButton } from "../common/RaisedButton";
import { compareInsensitive } from "../../utils/listUtil";
import { REQUEST_STATES } from "../common/commonStates";
import { DialogComponent } from "../common/Dialog.component";
import { adminUserDetailsPair } from "../common/RequesterPairs";
import adminActions from "./adminActions";
import { IAdminState } from "./AdminReducer";
import { withVerifyAccess } from "../common/withVerifyAccess";
import { multipleApiRequesterWrapper } from "../common/MultipleApiRequesterWrapper";
import { capitalizeFirstLetter } from "../../utils/commonUtil";
import { IApplicationRootState } from "../../../applicationState";
import {navigateTo} from "../../../navigateTo";

type IMainAdminComponentPropsFromReduxStore = IAdminState;

interface IMainAdminComponentActions {
    actions: { removeUser: typeof adminActions.removeUser };
}

export type MainAdminComponentProps = IMainAdminComponentPropsFromReduxStore & IMainAdminComponentActions;

interface IMainAdminComponentState {
    dialogOpen: boolean;
    userIdToDelete: string;
    userEmailToDelete: string;
}

const DEFAULT_VISIBLE_ROWS = 10;

export const MainAdminComponent: React.FC<MainAdminComponentProps> = (props) => {
    const navigate = useNavigate();
    const [state, setState] = useState<IMainAdminComponentState>({
        dialogOpen: false,
        userIdToDelete: "",
        userEmailToDelete: "",
    });

    const sortedUsers = useMemo(() => {
        if (props.adminUsersInfoState.kind !== REQUEST_STATES.REQUEST_SUCCEEDED) {
            return [];
        }
        return [...props.adminUsersInfoState.data].sort((a, b) => compareInsensitive(a.email, b.email));
    }, [props.adminUsersInfoState]);

    const displayedUsers = sortedUsers.slice(0, DEFAULT_VISIBLE_ROWS);
    const hasMore = sortedUsers.length > DEFAULT_VISIBLE_ROWS;

    const renderHeaders = () => (
        <tr className="admin-page__user-table-row admin-page__user-table-headers">
            <th className="admin-page__user-table-column admin-page__user-info__edit-link-header"/>
            <th className="admin-page__user-table-column admin-page__user-info__first-name-header">
                FIRST NAME
            </th>
            <th className="admin-page__user-table-column admin-page__user-info__last-name-header">
                LAST NAME
            </th>
            <th className="admin-page__user-table-column admin-page__user-info__email-header">
                EMAIL
            </th>
            <th className="admin-page__user-table-column admin-page__user-info__user-type-header">
                ROLE
            </th>
            <th className="admin-page__user-table-column admin-page__user-info__last-login-date-header">
                LAST LOGIN
            </th>
        </tr>
    );

    const renderUser = (user: IUserInfo, index: number) => (
        <tr className="admin-page__user-table-row admin-page__user-info"
            key={`admin-page__user-table-row-${index}`}>
            <td className="admin-page__user-table-column admin-page__user-info__edit-link">
                <Link to={`/admin/user/${user.arbtSecurityId}`} id={`edit-user-link-${user.arbtSecurityId}`}>Edit</Link>
            </td>
            <td className="admin-page__user-table-column admin-page__user-info__first-name">
                {user.firstName}
            </td>
            <td className="admin-page__user-table-column admin-page__user-info__last-name">
                {user.lastName}
            </td>
            <td className="admin-page__user-table-column admin-page__user-info__email">
                {user.email}
            </td>
            <td className="admin-page__user-table-column admin-page__user-info__user-type">
                {capitalizeFirstLetter(user.userType)}
            </td>
            <td className="admin-page__user-table-column admin-page__user-info__last-login-date">
                {user.lastLoginDate || "Never"}
            </td>
            <td className="admin-page__user-table-column admin-page__user-info__delete">
                <div className="fas fa-trash trash-can clickable" onClick={() => deleteUser(user)}/>
            </td>
        </tr>
    );

    const deleteUser = (user: IUserInfo) => {
        setState({
            dialogOpen: true,
            userIdToDelete: user.arbtSecurityId,
            userEmailToDelete: user.email,
        });
    };

    const handleClose = () => {
        setState((prev) => ({ ...prev, dialogOpen: false }));
    };

    const handleDelete = () => {
        props.actions.removeUser(state.userIdToDelete, state.userEmailToDelete);
        setState((prev) => ({ ...prev, dialogOpen: false }));
    };

    const renderResultCount = () => {
        if (hasMore) {
            return `Showing ${DEFAULT_VISIBLE_ROWS} of ${sortedUsers.length} users`;
        }
        return `${sortedUsers.length} users`;
    };

    const renderPage = () => (
        <div id="admin-page" className="new-common-styles" data-testid="admin-page">
            <div className="admin-page__headers">
                <h1 id="admin-page-title">User Administration</h1>
                <a className="flex-right-align link-16 clickable" onClick={() => {navigateTo("/admin/mass-assign-users", navigate);}}>
                    Mass Assign Users to Plans
                </a>
                <RaisedButton className="admin-page__add-user-button"
                              style={prominentButtonStyle}
                              primary={true}
                              onClick={() => {navigateTo("/admin/add-user", navigate);}}>
                    Add User
                </RaisedButton>
            </div>
            <div className="admin-page__spacer"/>
            <div className="admin-page__result-count" data-testid="admin-result-count">
                {renderResultCount()}
            </div>
            <table className="admin-page__user-table">
                <thead>{renderHeaders()}</thead>
                <tbody>{displayedUsers.map((user, index) => renderUser(user, index))}</tbody>
            </table>
            {hasMore && (
                <div className="admin-page__more-indicator" data-testid="admin-more-indicator">
                    <i className="fas fa-magnifying-glass" aria-hidden="true"/>
                    <span>
                        Showing {DEFAULT_VISIBLE_ROWS} of {sortedUsers.length} users
                    </span>
                </div>
            )}
            <DialogComponent
                title="Remove this user?"
                open={state.dialogOpen}
                noText="no, keep 'em"
                yesText="yes, remove 'em"
                onNo={handleClose}
                onYes={handleDelete}
                description="User will no longer have access to the website."
            />
        </div>
    );

    return props.adminUsersInfoState.kind === REQUEST_STATES.REQUEST_SUCCEEDED
        ? renderPage()
        : null;
};

export const mapDispatchToProps = (dispatch: Dispatch): IMainAdminComponentActions => ({
    actions: bindActionCreators({
        removeUser: adminActions.removeUser,
    }, dispatch),
});

export const mapStateToProps = (state: IApplicationRootState): IMainAdminComponentPropsFromReduxStore => ({
    ...state.adminState!,
});

const connectedComponent = connect<IMainAdminComponentPropsFromReduxStore, IMainAdminComponentActions>(
    mapStateToProps,
    mapDispatchToProps
)(MainAdminComponent);

const withRequester = multipleApiRequesterWrapper(
    connectedComponent,
    [
        adminUserDetailsPair,
    ],
);

export default withVerifyAccess(withRequester, UserTypeEnum.ADMIN);